// Base UI implementation shared by Select and MultiSelect.
import { Combobox } from "@base-ui/react/combobox";
import { Select as BaseSelect } from "@base-ui/react/select";
import { Check, ChevronDown } from "lucide-react";
import { useMemo } from "react";
import { twMerge } from "tailwind-merge";
import Checkbox from "../Checkbox";
import {
    FLOATING_CHOSEN_ROW_CLASS,
    FLOATING_PANEL_CLASS,
    FLOATING_POSITIONER_CLASS,
    FLOATING_ROW_CLASS,
    FLOATING_SCROLLING_LIST_CLASS,
    FLOATING_LIST_PANEL_CLASS,
    FloatingEmptyText,
    FloatingRowContent,
    FloatingRowCount,
} from "../FloatingPanel";
import type { SelectOption, SelectSharedProps, SelectValue } from "./types";

type OptionPickerProps<TValue extends SelectValue> = SelectSharedProps<TValue> & {
    multiple: boolean;
    /** A single value or null, or an array when multiple. */
    value: TValue | TValue[] | null;
    onValueChange: (value: TValue | TValue[] | null) => void;
    isSelected: (value: TValue) => boolean;
    /** Default trigger text. Undefined shows the placeholder. */
    triggerText: string | undefined;
};

const TRIGGER_CLASS = twMerge(
    // Value on the left, chevron on the right. min-w-0 lets a long value truncate instead of widening the trigger.
    "flex min-w-0 items-center justify-between gap-1",
    // Same size and look as Button's white style, so a select lines up with the buttons beside it.
    "cursor-pointer rounded border border-stone-200 bg-white px-2 py-[3px] font-medium shadow",
    // Tinted on hover, and stays tinted while the list is open.
    "hover:bg-stone-50 data-popup-open:bg-stone-50",
    "data-disabled:cursor-not-allowed data-disabled:opacity-50",
);

/** Props shared by Combobox.Trigger and Select.Trigger. */
function triggerProps<TValue extends SelectValue>(props: OptionPickerProps<TValue>) {
    // A custom trigger gets only the accessible name. The default trigger also gets its styling and text.
    if (props.trigger !== undefined) {
        return { "aria-label": props.label, render: props.trigger };
    }

    const placeholder = <span className="text-stone-400">{props.placeholder ?? "Select…"}</span>;

    return {
        "aria-label": props.label,
        className: twMerge(TRIGGER_CLASS, props.className),
        children: (
            <>
                <span className="min-w-0 truncate">
                    {props.triggerText ?? placeholder}
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 stroke-stone-400" />
            </>
        ),
    };
}

/** Check mark for single choice, leading checkbox for multiple. */
const OptionRowContent = <TValue extends SelectValue>(props: { option: SelectOption<TValue>; selected: boolean; multiple: boolean }) => (
    <FloatingRowContent
        icon={props.option.icon}
        description={props.option.description}
        leading={props.multiple ? <Checkbox checked={props.selected} disabled={props.option.disabled} /> : undefined}
        trailing={
            <>
                {props.option.count !== undefined && <FloatingRowCount count={props.option.count} />}
                {!props.multiple && props.selected && <Check className="h-4 w-4 stroke-stone-600" />}
            </>
        }
    >
        {props.option.label}
    </FloatingRowContent>
);

/** Combobox with a search box. Filters locally unless onSearchChange is set. */
const SearchablePicker = <TValue extends SelectValue>(props: OptionPickerProps<TValue>) => {
    // Builds the item list Base UI filters, keyed by option value.
    const items = useMemo(
        () => Combobox.createItems(props.options, { getValue: option => option.value, getLabel: option => option.label }),
        [props.options],
    );
    const emptyText = props.emptyText ?? (props.options.length === 0 ? "No options defined" : "No matches");

    return (
        <Combobox.Root
            multiple={props.multiple}
            items={items}
            value={props.value}
            onValueChange={value => props.onValueChange(value ?? null)}
            onOpenChange={open => props.onOpenChange?.(open)}
            disabled={props.disabled}
            autoHighlight
            // onSearchChange returns already-filtered options.
            filter={props.onSearchChange !== undefined ? null : undefined}
            onInputValueChange={(query, eventDetails) => {
                // Keeps the search text after a pick in multiple mode.
                if (props.multiple && eventDetails.isItemPress) {
                    eventDetails.cancel();
                    return;
                }
                props.onSearchChange?.(query);
            }}
        >
            <Combobox.Trigger {...triggerProps(props)} />
            <Combobox.Portal>
                <Combobox.Positioner className={FLOATING_POSITIONER_CLASS} sideOffset={4} align="start">
                    <Combobox.Popup className={twMerge(FLOATING_PANEL_CLASS, FLOATING_LIST_PANEL_CLASS, props.panelClassName)} aria-label={props.label}>
                        <Combobox.Input
                            className="mb-1 h-8 w-full shrink-0 rounded border border-stone-200 px-2 text-sm outline-none focus:border-bronze-600"
                            placeholder="Search…"
                        />
                        {/* No-matches message. empty:hidden avoids an extra gap above the list. */}
                        <Combobox.Empty className="empty:hidden">
                            <FloatingEmptyText>{emptyText}</FloatingEmptyText>
                        </Combobox.Empty>
                        <Combobox.List className={FLOATING_SCROLLING_LIST_CLASS}>
                            {(option: SelectOption<TValue>) => (
                                <Combobox.Item key={option.value} value={option.value} disabled={option.disabled} className={twMerge(FLOATING_ROW_CLASS, !props.multiple && FLOATING_CHOSEN_ROW_CLASS)}>
                                    <OptionRowContent option={option} selected={props.isSelected(option.value)} multiple={props.multiple} />
                                </Combobox.Item>
                            )}
                        </Combobox.List>
                    </Combobox.Popup>
                </Combobox.Positioner>
            </Combobox.Portal>
        </Combobox.Root>
    );
};

/** Select without search. Typing a letter jumps to a matching option. */
const ListPicker = <TValue extends SelectValue>(props: OptionPickerProps<TValue>) => (
    <BaseSelect.Root
        multiple={props.multiple}
        value={props.value}
        onValueChange={value => props.onValueChange(value)}
        onOpenChange={open => props.onOpenChange?.(open)}
        disabled={props.disabled}
    >
        <BaseSelect.Trigger {...triggerProps(props)} />
        <BaseSelect.Portal>
            {/* Opens below the trigger instead of over it like a native select. */}
            <BaseSelect.Positioner className={FLOATING_POSITIONER_CLASS} sideOffset={4} align="start" alignItemWithTrigger={false}>
                <BaseSelect.Popup className={twMerge(FLOATING_PANEL_CLASS, FLOATING_LIST_PANEL_CLASS, props.panelClassName)}>
                    <BaseSelect.List className={FLOATING_SCROLLING_LIST_CLASS}>
                        {props.options.length === 0 && <FloatingEmptyText>{props.emptyText ?? "No options defined"}</FloatingEmptyText>}
                        {props.options.map(option => (
                            <BaseSelect.Item
                                key={option.value}
                                value={option.value}
                                label={option.label}
                                disabled={option.disabled}
                                className={twMerge(FLOATING_ROW_CLASS, !props.multiple && FLOATING_CHOSEN_ROW_CLASS)}
                            >
                                <OptionRowContent option={option} selected={props.isSelected(option.value)} multiple={props.multiple} />
                            </BaseSelect.Item>
                        ))}
                    </BaseSelect.List>
                </BaseSelect.Popup>
            </BaseSelect.Positioner>
        </BaseSelect.Portal>
    </BaseSelect.Root>
);

/** Renders the searchable version when searchable or onSearchChange is set, otherwise the plain list. */
export const OptionPicker = <TValue extends SelectValue>(props: OptionPickerProps<TValue>) => {
    const isSearchable = props.searchable === true || props.onSearchChange !== undefined;

    if (isSearchable) {
        return <SearchablePicker {...props} />;
    }

    return <ListPicker {...props} />;
};
